import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useSafe } from './context/SafeContext';
import { isValidCampaignSrc } from './lib/campaignParams';
import AuthPage from './pages/AuthPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import UnlockPage from './pages/UnlockPage';
import ItemDetailPage from './pages/ItemDetailPage';
import ItemEditorPage from './pages/ItemEditorPage';
import OrgListPage from './pages/OrgListPage';
import OrgCreatePage from './pages/OrgCreatePage';
import { OrgDetailLayout, OrgDefaultRoute } from './pages/orgs/OrgDetailLayout';
import { OverviewTab } from './pages/orgs/OverviewTab';
import { ManageTab } from './pages/orgs/ManageTab';
import { UsageTab } from './pages/orgs/UsageTab';
import { CirclesTab } from './pages/orgs/CirclesTab';
import { CategoriesTab } from './pages/orgs/CategoriesTab';
import GeneratorPage from './pages/GeneratorPage';
import ImportPage from './pages/ImportPage';
import DataPage from './pages/DataPage';
import SettingsPage from './pages/SettingsPage';
import CheckupPage from './pages/CheckupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import RecoveryDenyPage from './pages/RecoveryDenyPage';
import RecoveryContactsPage from './pages/RecoveryContactsPage';
import RecoveryReadOnlySafePage from './pages/RecoveryReadOnlySafePage';
import VaultLayout from './components/VaultLayout';
import { EmergencyAccessBanner } from './components/EmergencyAccessBanner';
import { InboundRecoveryBanner } from './components/InboundRecoveryBanner';
import { PendingInviteShellBanner } from './components/PendingInviteShellBanner';
import EmptyDetailPanel from './components/EmptyDetailPanel';
import ContactsLayout from './components/ContactsLayout';
import ContactsEmptyPanel from './components/ContactsEmptyPanel';
import ContactDetailPage from './pages/ContactDetailPage';
import ContactEditorPage from './pages/ContactEditorPage';
import CardsLayout from './components/CardsLayout';
import CardsEmptyPanel from './components/CardsEmptyPanel';
import CardDetailPage from './pages/CardDetailPage';
import CardEditorPage from './pages/CardEditorPage';
import FamilyBinderPage from './pages/orgs/FamilyBinderPage';
import DocumentEditorPage from './pages/orgs/DocumentEditorPage';
import DocumentDetailPage from './pages/orgs/DocumentDetailPage';
import ListEditorPage from './pages/orgs/ListEditorPage';
import ListDetailPage from './pages/orgs/ListDetailPage';
import ScheduleEditorPage from './pages/orgs/ScheduleEditorPage';
import ScheduleDetailPage from './pages/orgs/ScheduleDetailPage';
import FamilyContactDetailPage from './pages/orgs/FamilyContactDetailPage';
import FamilyContactEditorPage from './pages/orgs/FamilyContactEditorPage';

/**
 * Where AuthGuard sends an unauthenticated visitor.
 *
 * Carries `src` — and ONLY `src` — through to /auth. Previously this redirected
 * to a bare `/auth`, silently dropping campaign attribution on every ad click
 * (AuthPage reads `src` from /auth's own query string, which was empty by then).
 *
 * Deliberately a whitelist rather than forwarding location.search wholesale: a
 * deep link like /safe/abc?q=<search terms> would otherwise spill the user's
 * search query into the auth URL.
 */
export function authRedirectTarget(search: string): string {
  try {
    const src = new URLSearchParams(search).get('src');
    if (isValidCampaignSrc(src)) {
      return `/auth?src=${encodeURIComponent(src)}`;
    }
  } catch {
    /* unparseable query → plain /auth */
  }
  return '/auth';
}

export function AuthGuard({ children }: { children: React.ReactNode }) {
  const { status } = useSafe();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-hbs-bg text-hbs-text-mute text-sm">
        Loading…
      </div>
    );
  }
  if (status === 'unauthenticated') {
    return <Navigate to={authRedirectTarget(location.search)} state={{ from: location }} replace />;
  }
  if (status === 'locked') {
    return <UnlockPage />;
  }
  // Shell banners live here, not in VaultLayout, because VaultLayout wraps only
  // /safe — so a designation or family invite arriving while the user sat on
  // Families, Settings or a Binder page produced no visible signal anywhere.
  // AuthGuard is the one wrapper every authenticated route already goes
  // through, which makes "every signed-in page shows pending actions" true by
  // construction rather than by remembering to add it to each layout.
  return (
    <>
      <EmergencyAccessBanner />
      <InboundRecoveryBanner />
      <PendingInviteShellBanner />
      {children}
    </>
  );
}

export default function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/safe" replace />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/safe" element={<AuthGuard><VaultLayout /></AuthGuard>}>
        <Route index element={<EmptyDetailPanel />} />
        <Route path="new" element={<ItemEditorPage />} />
        <Route path=":id" element={<ItemDetailPage />} />
        <Route path=":id/edit" element={<ItemEditorPage />} />
      </Route>
      <Route path="/contacts" element={<AuthGuard><ContactsLayout /></AuthGuard>}>
        <Route index element={<ContactsEmptyPanel />} />
        <Route path="new" element={<ContactEditorPage />} />
        <Route path=":id" element={<ContactDetailPage />} />
        <Route path=":id/edit" element={<ContactEditorPage />} />
      </Route>
      <Route path="/cards" element={<AuthGuard><CardsLayout /></AuthGuard>}>
        <Route index element={<CardsEmptyPanel />} />
        <Route path="new" element={<CardEditorPage />} />
        <Route path=":id" element={<CardDetailPage />} />
        <Route path=":id/edit" element={<CardEditorPage />} />
      </Route>
      {/* Family Binder sections reached from the sidebar's family node. The
          static `/contacts` path outranks the dynamic `:categoryId`. */}
      <Route path="/orgs/:orgId/binder/contacts" element={<AuthGuard><FamilyBinderPage section="contacts" /></AuthGuard>} />
      {/* Contact detail from the Family-contacts catch-all (no category). */}
      <Route path="/orgs/:orgId/binder/contacts/:itemId" element={<AuthGuard><FamilyContactDetailPage /></AuthGuard>} />
      {/* Static `documents`/`contacts` segments outrank the `:categoryId` catch below. */}
      <Route path="/orgs/:orgId/binder/:categoryId/documents/new" element={<AuthGuard><DocumentEditorPage /></AuthGuard>} />
      <Route path="/orgs/:orgId/binder/:categoryId/documents/:itemId/edit" element={<AuthGuard><DocumentEditorPage /></AuthGuard>} />
      <Route path="/orgs/:orgId/binder/:categoryId/documents/:itemId" element={<AuthGuard><DocumentDetailPage /></AuthGuard>} />
      <Route path="/orgs/:orgId/binder/:categoryId/schedules/new" element={<AuthGuard><ScheduleEditorPage /></AuthGuard>} />
      <Route path="/orgs/:orgId/binder/:categoryId/schedules/:itemId/edit" element={<AuthGuard><ScheduleEditorPage /></AuthGuard>} />
      <Route path="/orgs/:orgId/binder/:categoryId/schedules/:itemId" element={<AuthGuard><ScheduleDetailPage /></AuthGuard>} />
      <Route path="/orgs/:orgId/binder/:categoryId/lists/new" element={<AuthGuard><ListEditorPage /></AuthGuard>} />
      <Route path="/orgs/:orgId/binder/:categoryId/lists/:itemId/edit" element={<AuthGuard><ListEditorPage /></AuthGuard>} />
      <Route path="/orgs/:orgId/binder/:categoryId/lists/:itemId" element={<AuthGuard><ListDetailPage /></AuthGuard>} />
      <Route path="/orgs/:orgId/binder/contacts/:itemId/edit" element={<AuthGuard><FamilyContactEditorPage /></AuthGuard>} />
      <Route path="/orgs/:orgId/binder/:categoryId/contacts/:itemId/edit" element={<AuthGuard><FamilyContactEditorPage /></AuthGuard>} />
      <Route path="/orgs/:orgId/binder/:categoryId/contacts/:itemId" element={<AuthGuard><FamilyContactDetailPage /></AuthGuard>} />
      <Route path="/orgs/:orgId/binder/:categoryId" element={<AuthGuard><FamilyBinderPage /></AuthGuard>} />
      <Route path="/orgs" element={<AuthGuard><OrgListPage /></AuthGuard>} />
      <Route path="/orgs/new" element={<AuthGuard><OrgCreatePage /></AuthGuard>} />
      <Route path="/orgs/:orgId" element={<AuthGuard><OrgDetailLayout /></AuthGuard>}>
        {/* Manage is the family's default page; a member without access to it
            is sent to Overview instead. Overview and Usage are no longer in
            the tab strip but keep their routes, so a bookmark or a shared link
            still opens them. */}
        <Route index element={<OrgDefaultRoute />} />
        <Route path="manage" element={<ManageTab />} />
        <Route path="overview" element={<OverviewTab />} />
        <Route path="circles" element={<CirclesTab />} />
        <Route path="categories" element={<CategoriesTab />} />
        <Route path="usage" element={<UsageTab />} />
        <Route path="items/:itemId" element={<ItemDetailPage />} />
        <Route path="items/:itemId/edit" element={<ItemEditorPage />} />
      </Route>
      <Route path="/import" element={<AuthGuard><ImportPage /></AuthGuard>} />
      <Route path="/data" element={<AuthGuard><DataPage /></AuthGuard>} />
      <Route path="/tools" element={<AuthGuard><GeneratorPage /></AuthGuard>} />
      <Route path="/checkup" element={<AuthGuard><CheckupPage /></AuthGuard>} />
      <Route path="/settings" element={<AuthGuard><SettingsPage /></AuthGuard>} />
      <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
      {/* Public, no-login: the owner's one-click deny link from the recovery
          email. The signed token in the URL is the authorization. A fully-static
          path, so it outranks the guarded `/safe/recovery/:protectedUserId`. */}
      <Route path="/safe/recovery/deny" element={<RecoveryDenyPage />} />
      <Route path="/settings/recovery-contacts" element={<AuthGuard><RecoveryContactsPage /></AuthGuard>} />
      <Route path="/safe/recovery/:protectedUserId" element={<AuthGuard><VaultLayout /></AuthGuard>}>
        <Route index element={<RecoveryReadOnlySafePage />} />
      </Route>
    </Routes>
  );
}
